import axios from "axios";
import { RENDER_URL } from "../config/api";
import { attachActor } from "./actor";

// ---------------------------------------------------------------------------
// Service Provider Status
// ---------------------------------------------------------------------------

interface ServiceStatusIncident {
  id: string;
  title: string;
  provider: string;
  service: string;
  latestStatus: string;
  link: string;
  pubDate: string;
}

export interface ServiceStatus {
  hasActiveIncidents: boolean;
  incidents: ServiceStatusIncident[];
  userMessage: string | null;
  lastChecked: string | null;
  lastUpdated: string | null;
}

// Dedicated backend-only client for service-status (bypasses apiClient's interceptors)
const backendStatusClient = (() => {
  // In dev, hit the backend directly (Vite proxy may not be configured for this).
  // In production, hit Render directly.
  const devBackend = import.meta.env.VITE_API_URL_FALLBACK || 'http://localhost:3000';
  const base = import.meta.env.DEV ? (devBackend + '/api') : (RENDER_URL + '/api');
  const client = axios.create({ baseURL: base, timeout: 10000 });
  // Name the acting team member, like every other backend call.
  client.interceptors.request.use(attachActor);
  return client;
})();

/**
 * Fetch current service provider status (Render / Vercel health)
 */
export const getServiceStatus = async (): Promise<ServiceStatus> => {
  try {
    const response = await backendStatusClient.get('/service-status');
    return response.data;
  } catch {
    // Silently return "no issues" when the endpoint is unreachable
    return {
      hasActiveIncidents: false,
      incidents: [],
      userMessage: null,
      lastChecked: null,
      lastUpdated: null
    };
  }
};

/**
 * Demo mode: fetch a simulated incident for visual testing.
 * Triggered by ?statusDemo=true in the URL.
 */
export const getServiceStatusDemo = async (): Promise<ServiceStatus> => {
  try {
    const response = await backendStatusClient.get('/service-status?demo=true');
    return response.data;
  } catch {
    return {
      hasActiveIncidents: false,
      incidents: [],
      userMessage: null,
      lastChecked: null,
      lastUpdated: null
    };
  }
};
